import { inject } from '@angular/core';
import { ActivityEntityName, EntityViewDisplay, FeatureFlags, FinanceSecurityRight, SaleOrder, SecurityModuleTag, SurfaceType } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { entityRecordUrl } from '@workbuddy/ui/v2/blocks/entity.record';
import { saleOrderStatusTag } from './presentation/sale.order.status.colour';

// Registered when this manifest module is loaded at bootstrap. Consumers resolve manifests via
// getManifest/EntityManifestService only.
// This is the Tenant manifest; portals get no Sale Order UI until declared under `domains`.
//
// Surface type is FinancialDocument (not SurfaceType.Manage) — that is the Sales Order "manage"
// form. ManageSaleOrder is the write *permission*, not a surface.
export const saleOrderManifest = registerManifest<SaleOrder>({
  entity: ActivityEntityName.SaleOrder, slug: 'sales-orders',
  featureFlag: FeatureFlags.FinanceDocsSalesOrderV2,
  menu: {
    group: 'finance',
    label: 'Sales Orders',
    icon: 'fa-money-check-dollar',
    order: 30,
    badge: SecurityModuleTag.Beta,
    authorize: () => inject(RightsService).hasRights(FinanceSecurityRight.CanAccessSaleOrder),
  },

  routes: {
    root: '/finance/sales-order',
    record: id => entityRecordUrl('sales-orders', 'preview', id),
  },

  // tags feeds the BOARD CARD's status badge; the surface header reads the same colours
  // through the same saleOrderStatusTag function, so card and header cannot drift apart.
  presentation: { tags: { status: saleOrderStatusTag } },

  board: {
    // Kanban is not meaningful for financial documents; locked manage preview is a per-view toggle.
    supportedDisplays: [EntityViewDisplay.Card, EntityViewDisplay.Table],
    // Document-shaped: the selection shows in the wide ~50/50 aside, not a narrow quickview.
    panel: { kind: 'preview' },
    defaults: { header: { title: 'Sales Orders' } },
  },

  surfaces: {
    [SurfaceType.FinancialDocument]: () => import('./surface/manage/sale.order.manage.surface').then(m => m.saleOrderSurface),
  },
  operations: {
    preview: {
      surface: SurfaceType.FinancialDocument,
      authorize: ({ injector }) => injector.get(RightsService).hasRights(FinanceSecurityRight.CanAccessSaleOrder),
    },
    create: {
      surface: SurfaceType.FinancialDocument,
      authorize: ({ injector }) => injector.get(RightsService).hasRights(FinanceSecurityRight.ManageSaleOrder),
    },
    // Read access mounts the document; write rights and status belong to the surface.
    edit: {
      surface: SurfaceType.FinancialDocument,
      authorize: ({ injector }) => injector.get(RightsService).hasRights(FinanceSecurityRight.CanAccessSaleOrder),
    },
  },

  relations: {
    [ActivityEntityName.Activity]: () => import('./relations/sale.order.activities.relation').then(m => m.saleOrderActivitiesBoardBuilder),
  },
});
